import { Family, MemberPlan, ModuleKey, PlanItem, Role, TaskTemplate } from '../types/family';
import { memberPalette, neutralHouseCategories, neutralRewards, neutralRules } from '../data/neutralTemplate';
import { uid } from './db';
import { eveningTask, houseTask, learningTask, planItem, readingTask, roomWeekTask, sportTask } from './templateBuilders';

export interface WizardMember {
  name: string;
  role: Role;
  birth?: string;
  contact?: string;
}

export interface WizardInput {
  name: string;
  members: WizardMember[];
  modules: Record<ModuleKey, boolean>;
  start: 'prazno' | 'predloga';
}

export function buildFamily(input: WizardInput): Family {
  const id = uid('fam');
  const members = input.members.map((m, i) => ({
    id: `${id}-m${i}`,
    name: m.name.trim(),
    role: m.role,
    color: memberPalette[i % memberPalette.length],
    birth: m.birth || undefined,
    contact: m.contact || undefined,
    invite: i === 0 ? ('sprejeto' as const) : m.contact ? ('poslano' as const) : ('brez' as const),
  }));
  const kids = members.filter((m) => m.role === 'otrok').map((m) => m.id);
  const everyone = members.map((m) => m.id);
  const rules = { ...neutralRules };
  let templates: TaskTemplate[] = [];
  let plans: MemberPlan[] = members.map((m) => ({ memberId: m.id, items: [] }));

  if (input.start === 'predloga') {
    if (input.modules.osebnaRutina) {
      templates.push(...everyone.map((m) => readingTask(m, rules.readingMinutes)), ...everyone.map((m) => sportTask(m, rules.sportMinutes)));
    }
    if (input.modules.sola) templates.push(...kids.map(learningTask));
    if (input.modules.vecerniRezim) templates.push(...kids.map(eveningTask));
    if (input.modules.hisnaOpravila) {
      templates.push(...kids.map(roomWeekTask));
      neutralHouseCategories.forEach((title, i) => {
        const who = everyone.length > 0 ? [everyone[i % everyone.length]] : [];
        templates.push(houseTask(`${id}-h${i}`, title, who, rules.houseCategoryValue));
      });
    }
    plans = members.map((m) => {
      const child = m.role === 'otrok';
      const items: PlanItem[] = [];
      if (input.modules.osebnaRutina) items.push(planItem('branje', 'Branje', 5, 7, 0), planItem('sport', 'Šport', 5, 3, 0));
      if (child && input.modules.sola) items.push(planItem('ucenje', 'Domače naloge in učni plan', 5, 5, 0));
      if (child && input.modules.vecerniRezim) items.push(planItem('vecer', 'Večerna rutina', 2, 7, 0));
      if (child && input.modules.hisnaOpravila) items.push(planItem('soba', 'Soba – sobotni pregled', 15, 1, 0));
      return { memberId: m.id, items };
    });
  }

  if (!input.modules.osebnaRutina) templates = templates.filter((t) => t.module !== 'osebnaRutina');

  return {
    id,
    name: input.name.trim(),
    sampleLabel: 'Nova družina – ustvarjena v prototipu',
    createdInPrototype: true,
    members,
    modules: input.modules,
    rules,
    templates,
    plans,
    animals: [],
    events: [],
    rewards: input.start === 'predloga' ? neutralRewards.map((r, i) => ({ ...r, id: `${id}-r${i}` })) : [],
    calendars: [],
    bonusSeed: {},
    lastWeekReached: {},
    goal: null,
  };
}
